/**
 * Hoja de la orden de compra (spec 21): carta vertical con el membrete de la empresa, el proveedor, a dónde debe
 * llegar, los productos con sus cantidades (sin precios) y el pedido de cotización. Imprimir → «Guardar como PDF» para
 * mandarla por otra vía. Cada apertura se cuenta y queda en la auditoría; la hoja no dice «reimpresión».
 */
import { formatearCantidad, Money, UNIDAD_MEDIDA_ETIQUETA } from '@mg/shared';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { Membrete, useEmpresaImpresion } from '@/componentes/Impresion.tsx';
import { mensajeDeError } from '@/lib/api.ts';
import { useRegistroImpresion } from '@/lib/impresion.ts';
import { formatearFecha } from '@/lib/utils.ts';
import { registrarImpresionOrden, useOrdenCompra } from './api.ts';

const unidad = (u: string) => UNIDAD_MEDIDA_ETIQUETA[u as keyof typeof UNIDAD_MEDIDA_ETIQUETA] ?? u;

export function ImprimirOrdenCompra() {
  const { id } = useParams<{ id: string }>();
  const orden = useOrdenCompra(id ?? null);
  const empresa = useEmpresaImpresion();
  const registrada = useRegistroImpresion(id, (x) => registrarImpresionOrden(x).then(() => false));
  useEffect(() => {
    if (orden.data && empresa.data && registrada !== null) { const t = setTimeout(() => window.print(), 300); return () => clearTimeout(t); }
    return undefined;
  }, [orden.data, empresa.data, registrada]);
  if (orden.error) return <p style={{ padding: 24 }} role="alert">No se pudo abrir la orden de compra: {mensajeDeError(orden.error)}</p>;
  if (!orden.data || !empresa.data || registrada === null) return <p style={{ padding: 24, fontFamily: 'Georgia, serif' }}>Preparando orden de compra…</p>;
  const o = orden.data;
  return (
    <div className="formato" data-testid="formato-orden-compra" data-estado={o.estado}>
      <style>{`
        @page { size: letter; margin: 12mm; }
        html, body { background: #fff; margin: 0; }
        .formato { font-family: Arial, Helvetica, sans-serif; color: #000; font-size: 11px; width: 7.5in; margin: 0 auto; padding: 4px; position: relative; }
        .formato table { width: 100%; border-collapse: collapse; }
        .formato th, .formato td { padding: 3px 5px; vertical-align: top; }
        .formato th { border-top: 1px solid #000; border-bottom: 1px solid #000; text-align: left; }
        .formato .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
        .formato .marca { position: absolute; top: 35%; left: 18%; font-size: 64px; opacity: 0.12; transform: rotate(-20deg); font-weight: bold; letter-spacing: 4px; }
        @media screen { body { background: #e5e7eb; } .formato { background: #fff; box-shadow: 0 0 8px rgba(0,0,0,.2); margin: 16px auto; padding: 14px; } }
      `}</style>
      {o.estado === 'anulada' ? <div className="marca" data-testid="orden-anulada">ANULADA</div> : null}
      <Membrete empresa={empresa.data} />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, margin: '6px 0 8px' }}>
        <div style={{ flex: 1 }}>
          <div><b>Proveedor:</b> {o.proveedor_razon_social} · <b>RIF:</b> {o.proveedor_rif}</div>
          {o.proveedor_direccion_fiscal ? <div><b>Dirección:</b> {o.proveedor_direccion_fiscal}</div> : null}
          {o.proveedor_telefono || o.proveedor_correo ? <div>{o.proveedor_telefono ? <><b>Teléfono:</b> {o.proveedor_telefono} </> : null}{o.proveedor_correo ? <><b>Correo:</b> {o.proveedor_correo}</> : null}</div> : null}
          {o.almacen_nombre ? <div data-testid="orden-entrega"><b>Entregar en:</b> {o.almacen_nombre} ({o.sucursal_nombre})</div> : null}
        </div>
        <div style={{ textAlign: 'right', minWidth: 200 }}>
          <div style={{ fontWeight: 'bold', fontSize: 15 }}>ORDEN DE COMPRA</div>
          <div>Solicitud de cotización</div>
          <div>N° <b data-testid="orden-numero">{o.numero_formateado}</b></div>
          <div>Fecha: {formatearFecha(o.creado_en)}</div>
        </div>
      </div>
      <table>
        <thead><tr><th style={{ width: '18%' }}>Código</th><th>Descripción</th><th className="num">Cantidad</th>{o.entregas > 0 ? <><th className="num">Recibido</th><th className="num">Pendiente</th></> : null}</tr></thead>
        <tbody>
          {o.lineas.map((l) => (
            <tr key={l.id} data-testid="orden-renglon">
              <td>{l.producto_codigo}</td>
              <td>{l.producto_nombre}{l.nota ? <div style={{ fontSize: 9.5 }}>{l.nota}</div> : null}</td>
              <td className="num">{formatearCantidad(Money.parse(l.cantidad))} {unidad(l.unidad)}</td>
              {o.entregas > 0 ? <><td className="num">{formatearCantidad(Money.parse(l.recibido))}</td><td className="num" data-testid="orden-pendiente"><b>{formatearCantidad(Money.parse(l.pendiente))}</b></td></> : null}
            </tr>
          ))}
        </tbody>
      </table>
      {o.observaciones ? <p style={{ marginTop: 8 }} data-testid="orden-observaciones"><b>Observaciones:</b> {o.observaciones}</p> : null}
      <p style={{ marginTop: 10 }}>{o.entregas > 0 ? 'Ya recibimos parte de esta orden. Por favor, indíquenos cuándo entregan lo pendiente.' : 'Por favor, envíenos su cotización con precios y disponibilidad de estos productos.'}</p>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 36, fontSize: 10 }}>
        <span>Elaborada por: {o.creado_por_nombre}</span>
        <span>Recibido: ______________________</span>
      </div>
    </div>
  );
}
